import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id, Doc } from '@convex/_generated/dataModel'

// Re-export Convex types for convenience
export type ConvexPropertyDocument = Doc<'property_documents'>
export type PropertyDocumentId = Id<'property_documents'>
export type PropertyId = Id<'properties'>
export type StorageId = Id<'_storage'>

/** File types supported by property documents */
export type DocumentFileType = 'pdf' | 'doc' | 'image' | 'other'

/**
 * PropertyDocument type used by components.
 * Maps Convex fields to a component-friendly interface.
 */
export interface PropertyDocument {
  id: string // Maps to _id
  property_id: string
  storage_id: string
  url: string
  name: string
  description: string | null
  file_type: DocumentFileType
  file_size: number | null
  sort_order: number
  created_at: number
}

/**
 * Convert a Convex property_documents document to the component PropertyDocument type.
 */
export function toPropertyDocument(doc: ConvexPropertyDocument): PropertyDocument {
  return {
    id: doc._id,
    property_id: doc.property_id,
    storage_id: doc.storage_id,
    url: doc.url,
    name: doc.name,
    description: doc.description ?? null,
    file_type: doc.file_type,
    file_size: doc.file_size ?? null,
    sort_order: doc.sort_order,
    created_at: doc.created_at,
  }
}

/**
 * Determine file type from MIME type or file extension.
 */
export function getFileType(file: File): DocumentFileType {
  const mimeType = file.type.toLowerCase()
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''

  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf'
  }

  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    ['doc', 'docx', 'odt', 'rtf'].includes(extension)
  ) {
    return 'doc'
  }

  if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'image'
  }

  return 'other'
}

/**
 * Hook for fetching all documents for a property.
 * Uses Convex's real-time queries - data updates automatically when it changes.
 * Returns documents converted to the component PropertyDocument type.
 */
export function usePropertyDocuments(propertyId: PropertyId | string | undefined) {
  const rawData = useQuery(
    api.documents.listByProperty,
    propertyId ? { property_id: propertyId as PropertyId } : 'skip'
  ) as ConvexPropertyDocument[] | undefined

  // Convert Convex documents to component-friendly PropertyDocument type
  const data = rawData?.map((doc: ConvexPropertyDocument) => toPropertyDocument(doc))

  return {
    data,
    isLoading: rawData === undefined && propertyId !== undefined,
    error: null as Error | null, // Convex throws on error
  }
}

/**
 * Hook for generating an upload URL for Convex storage.
 * Returns a mutation function that returns a URL for uploading files.
 */
export function useGenerateDocumentUploadUrl() {
  const generateMutation = useMutation(api.documents.generateUploadUrl)

  const mutate = async () => {
    return await generateMutation({})
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for saving an uploaded document to the property_documents table.
 * Call this after uploading a file to the storage URL returned by useGenerateDocumentUploadUrl.
 */
export function useSaveDocument() {
  const saveMutation = useMutation(api.documents.saveDocument)

  const mutate = async ({
    propertyId,
    storageId,
    name,
    description,
    fileType,
    fileSize,
  }: {
    propertyId: PropertyId | string
    storageId: Id<'_storage'>
    name: string
    description?: string
    fileType: DocumentFileType
    fileSize?: number
  }) => {
    return await saveMutation({
      property_id: propertyId as PropertyId,
      storage_id: storageId,
      name,
      description,
      file_type: fileType,
      file_size: fileSize,
    })
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for uploading a document to Convex storage.
 * Combines generateUploadUrl and saveDocument into a single operation.
 */
export function useUploadDocument() {
  const generateUploadUrl = useMutation(api.documents.generateUploadUrl)
  const saveDocument = useMutation(api.documents.saveDocument)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [progress, setProgress] = useState<number | null>(null)

  const mutateAsync = useCallback(async ({
    propertyId,
    file,
    name,
    description,
  }: {
    propertyId: PropertyId | string
    file: File
    name?: string
    description?: string
  }) => {
    setIsPending(true)
    setError(null)
    setProgress(0)

    try {
      // Step 1: Get upload URL
      const uploadUrl = await generateUploadUrl({})
      setProgress(25)

      // Step 2: Upload file to storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      if (!response.ok) {
        throw new Error('Failed to upload file')
      }

      const { storageId } = await response.json()
      setProgress(75)

      // Step 3: Save document record with metadata
      const fileType = getFileType(file)
      const documentId = await saveDocument({
        property_id: propertyId as PropertyId,
        storage_id: storageId,
        name: name ?? file.name,
        description,
        file_type: fileType,
        file_size: file.size,
      })

      setProgress(100)
      return documentId
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      // Reset progress after a short delay
      setTimeout(() => setProgress(null), 500)
    }
  }, [generateUploadUrl, saveDocument])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
    progress,
  }
}

/**
 * Hook for deleting a document.
 * Removes both the storage file and the database record.
 */
export function useDeleteDocument() {
  const deleteMutation = useMutation(api.documents.remove)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const variablesRef = useRef<{ id: string; propertyId?: string } | null>(null)

  const mutateAsync = useCallback(async ({ id, propertyId }: { id: PropertyDocumentId | string; propertyId?: string }) => {
    setIsPending(true)
    setError(null)
    variablesRef.current = { id: id as string, propertyId }
    try {
      const result = await deleteMutation({
        id: id as PropertyDocumentId,
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      variablesRef.current = null
    }
  }, [deleteMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
    variables: variablesRef.current,
  }
}

/**
 * Hook for reordering documents for a property.
 * Takes an array of document IDs in the new order.
 */
export function useReorderDocuments() {
  const reorderMutation = useMutation(api.documents.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    propertyId,
    documentIds,
  }: {
    propertyId: PropertyId | string
    documentIds: (PropertyDocumentId | string)[]
  }) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        property_id: propertyId as PropertyId,
        document_ids: documentIds as PropertyDocumentId[],
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for updating document order (alias for backwards compatibility).
 * Maps from the old API shape to the new Convex-based shape.
 */
export function useUpdateDocumentOrder() {
  const reorderMutation = useMutation(api.documents.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    items,
    propertyId,
  }: {
    items: { id: string; sort_order: number }[]
    propertyId: string
  }) => {
    setIsPending(true)
    setError(null)
    try {
      // Sort items by sort_order and extract just the IDs
      const sortedIds = items
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((item) => item.id as PropertyDocumentId)

      const result = await reorderMutation({
        property_id: propertyId as PropertyId,
        document_ids: sortedIds,
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}
